import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function CustomsClearancePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: 'ri-file-list-3-line',
      title: 'Documentation Preparation',
      description: 'Complete customs documentation including commercial invoices, bills of lading, and declarations'
    },
    {
      icon: 'ri-calculator-line',
      title: 'Duty Calculation',
      description: 'Accurate assessment of customs duties, taxes, and tariffs based on product classification'
    },
    {
      icon: 'ri-government-line',
      title: 'Customs Liaison',
      description: 'Direct communication with customs authorities for smooth clearance processes'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Compliance Management',
      description: 'Ensuring adherence to all import-export regulations and trade compliance requirements'
    },
    {
      icon: 'ri-book-open-line',
      title: 'HS Code Classification',
      description: 'Proper harmonized system code assignment for accurate tariff determination'
    },
    {
      icon: 'ri-time-line',
      title: 'Fast-Track Clearance',
      description: 'Expedited clearance services to minimize delays and storage costs at ports'
    }
  ];

  const benefits = [
    'Zero clearance delays with expert documentation',
    'Accurate duty calculations preventing overpayment',
    'Full regulatory compliance and legal protection',
    'Direct port and customs authority coordination',
    'Real-time clearance status updates',
    'Cost-effective clearance solutions'
  ];

  const countries = [
    { name: 'United States', code: 'US' },
    { name: 'United Kingdom', code: 'GB' },
    { name: 'European Union', code: 'EU' },
    { name: 'United Arab Emirates', code: 'AE' },
    { name: 'Saudi Arabia', code: 'SA' },
    { name: 'Singapore', code: 'SG' },
    { name: 'Japan', code: 'JP' },
    { name: 'South Korea', code: 'KR' },
    { name: 'Australia', code: 'AU' },
    { name: 'Canada', code: 'CA' },
    { name: 'Malaysia', code: 'MY' },
    { name: 'Indonesia', code: 'ID' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/b857441a8282eff6e67d45b8b6cd5de0.jpeg" alt="Horizon Pathways Logo" className="w-12 h-12 object-contain" />
              <span className="text-xl font-semibold text-gray-900">Horizon Pathways</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Home</Link>
              <Link to="/products" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Products</Link>
              <a href="#services" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Services</a>
              <a href="#about" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">About</a>
              <a href="#contact" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Contact</a>
            </div>
            <button className="px-6 py-2 border-2 border-emerald-600 text-emerald-600 rounded-full text-sm font-medium transition-all hover:bg-emerald-600 hover:text-white whitespace-nowrap">Get Quote</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-8 relative overflow-hidden" style={{ backgroundColor: '#F8F6F3' }}>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-600 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full mb-6">
            <i className="ri-government-line text-emerald-700"></i>
            <span className="text-sm font-semibold text-emerald-700 uppercase tracking-wider">Customs Clearance Services</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
            Hassle-Free <br />
            <span className="italic font-serif text-emerald-700">Customs Clearance</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-10">
            Navigate complex customs regulations effortlessly with our expert clearance services. We handle all documentation, duties, and compliance requirements to ensure your shipments clear customs smoothly and quickly.
          </p>
          <div className="flex items-center justify-center gap-4">
            <button className="px-8 py-4 bg-emerald-600 text-white rounded-full text-base font-semibold hover:bg-emerald-700 transition-all whitespace-nowrap">
              Get Clearance Support
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <button className="px-8 py-4 border-2 border-gray-900 text-gray-900 rounded-full text-base font-semibold hover:bg-gray-900 hover:text-white transition-all whitespace-nowrap">
              Calculate Duties
              <i className="ri-calculator-line ml-2"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-amber-700 tracking-widest uppercase mb-4">OUR SERVICES</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Complete Customs Solutions</h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">End-to-end customs clearance services for seamless international trade</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="p-8 rounded-2xl bg-white border-2 border-gray-100 hover:border-emerald-600 hover:shadow-xl transition-all duration-300 group">
                <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-600 transition-all">
                  <i className={`${service.icon} text-3xl text-emerald-600 group-hover:text-white transition-all`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-8" style={{ backgroundColor: '#F8F6F3' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://public.readdy.ai/ai/img_res/3ca7f687935fce4cdc06886380ad5203.jpg" alt="Customs Clearance" className="w-full h-[500px] object-cover object-top rounded-3xl shadow-2xl" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-700 tracking-widest uppercase mb-4">WHY CHOOSE US</p>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Expert Customs Brokers</h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">Our licensed customs brokers have extensive experience in international trade regulations and maintain strong relationships with customs authorities worldwide. We ensure your shipments clear customs without delays.</p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-sm text-white"></i>
                    </div>
                    <span className="text-sm text-gray-700 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
              <button className="mt-8 px-8 py-4 bg-emerald-600 text-white rounded-full text-base font-semibold hover:bg-emerald-700 transition-all whitespace-nowrap">
                Contact Our Customs Team
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-emerald-400 tracking-widest uppercase mb-4">CLEARANCE PROCESS</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">How Customs Clearance Works</h2>
            <p className="text-gray-400 text-base max-w-2xl mx-auto">A systematic approach ensuring fast and compliant customs clearance</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">01</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Document Review</h3>
              <p className="text-sm text-gray-400 leading-relaxed">We review and verify all shipment documents for accuracy and completeness</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">02</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Declaration Filing</h3>
              <p className="text-sm text-gray-400 leading-relaxed">Electronic submission of customs declarations with proper classification</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">03</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Duty Payment</h3>
              <p className="text-sm text-gray-400 leading-relaxed">Processing of customs duties and tax payments on your behalf</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">04</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Release Authorization</h3>
              <p className="text-sm text-gray-400 leading-relaxed">Obtaining customs clearance approval and cargo release documentation</p>
            </div>
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-amber-700 tracking-widest uppercase mb-4">GLOBAL EXPERTISE</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Countries We Serve</h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">Expert customs clearance services across major import-export destinations</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {countries.map((country, index) => (
              <div key={index} className="p-6 rounded-xl bg-gray-50 hover:bg-emerald-50 transition-all text-center group cursor-pointer">
                <div className="text-3xl mb-3">{country.code === 'US' ? '🇺🇸' : country.code === 'GB' ? '🇬🇧' : country.code === 'EU' ? '🇪🇺' : country.code === 'AE' ? '🇦🇪' : country.code === 'SA' ? '🇸🇦' : country.code === 'SG' ? '🇸🇬' : country.code === 'JP' ? '🇯🇵' : country.code === 'KR' ? '🇰🇷' : country.code === 'AU' ? '🇦🇺' : country.code === 'CA' ? '🇨🇦' : country.code === 'MY' ? '🇲🇾' : '🇮🇩'}</div>
                <p className="text-sm font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">{country.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-8" style={{ backgroundColor: '#B8A8D4' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Simplify Your Customs Clearance Today</h2>
          <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto">Avoid delays and complications with our professional customs brokerage services. Get expert support for hassle-free clearance.</p>
          <button className="px-10 py-4 bg-white text-gray-900 rounded-full text-base font-semibold hover:bg-gray-100 transition-all whitespace-nowrap">
            Start Clearance Process
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
